import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAdminAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Users as UsersIcon, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  ADMIN_MASTER: { label: "Admin Master", cor: "bg-foreground text-background" },
  GERENCIA: { label: "Gerência", cor: "bg-info/10 text-info border-info/40" },
  OPERACIONAL: { label: "Operacional", cor: "bg-secondary text-secondary-foreground" },
};

export default function AdminUsuarios() {
  const { admin, checkSession } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "OPERACIONAL" });
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", role: "OPERACIONAL" });

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(extractError(e) || "Falha ao carregar");
    }
  };
  useEffect(() => { load(); }, []);

  const criar = async () => {
    try {
      await api.post("/users", form);
      toast.success("Usuário criado");
      setModalOpen(false);
      setForm({ name: "", email: "", password: "", role: "OPERACIONAL" });
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao criar");
    }
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "OPERACIONAL",
    });
    setEditOpen(true);
  };

  const salvarEdicao = async () => {
    try {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editingUser.id}`, payload);
      toast.success("Usuário atualizado");
      setEditOpen(false);
      setEditingUser(null);
      // Se editou a si mesmo, recarrega o contexto
      if (editingUser.id === admin?.id && checkSession) {
        await checkSession();
      }
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao atualizar");
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Excluir "${name}"?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuário excluído");
      // Se excluiu a si mesmo, faz logout redirecionando
      if (id === admin?.id) {
        window.location.href = "/admin/login";
        return;
      }
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao excluir");
    }
  };

  const totalMasters = users.filter((u) => u.role === "ADMIN_MASTER").length;

  if (admin?.role !== "ADMIN_MASTER") {
    return (
      <div className="text-center py-16 border border-dashed border-border rounded-sm">
        <ShieldCheck size={40} className="mx-auto text-muted-foreground mb-4" />
        <div className="font-heading font-bold text-lg">Acesso restrito</div>
        <div className="text-sm text-muted-foreground mt-1">Apenas Admin Master pode gerenciar usuários.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-tag mb-2">Controle de acesso administrativo · {totalMasters} Admin Master(s) ativo(s)</div>
          <h2 className="font-heading font-black tracking-tighter text-3xl">Usuários</h2>
        </div>
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-novo-usuario" className="rounded-sm bg-foreground text-background hover:bg-foreground/90">
              <Plus size={14} className="mr-2" /> Novo usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>Cadastrar novo usuário</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-tag">Nome completo</Label>
                <Input data-testid="user-nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="label-tag">E-mail</Label>
                <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="label-tag">Senha (mín 6 caracteres)</Label>
                <Input data-testid="user-senha" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div>
                <Label className="label-tag">Nível de acesso</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPERACIONAL">Operacional (obras / unidades / portaria)</SelectItem>
                    <SelectItem value="GERENCIA">Gerência (operacional + financeiro)</SelectItem>
                    <SelectItem value="ADMIN_MASTER">Admin Master (acesso total)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button data-testid="user-salvar" onClick={criar} className="bg-foreground text-background hover:bg-foreground/90">Cadastrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {totalMasters <= 1 && (
        <div className="text-xs border border-warning/40 bg-warning/5 text-warning-foreground/90 px-4 py-3 rounded-sm">
          <b>Atenção:</b> existe apenas <b>1 Admin Master</b> ativo. Cadastre outro Admin Master antes de excluir ou rebaixar o único existente para não perder acesso ao sistema.
        </div>
      )}

      <Card className="p-6 rounded-sm border-border">
        {users.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">Nenhum usuário cadastrado.</div>
        ) : (
          <div className="grid gap-2">
            {users.map((u) => {
              const meta = ROLE_LABELS[u.role] || { label: u.role, cor: "bg-secondary" };
              const podeExcluir = !(u.role === "ADMIN_MASTER" && totalMasters <= 1);
              const isSelf = u.id === admin?.id;
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between border border-border p-4 rounded-sm gap-3 min-w-0">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 bg-secondary flex items-center justify-center rounded-sm flex-shrink-0">
                      <UsersIcon size={14} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold break-anywhere flex items-center gap-2">
                        {u.name}
                        {isSelf && <Badge variant="outline" className="text-[9px] uppercase tracking-widest">você</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono break-anywhere">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={`${meta.cor} text-[10px] uppercase tracking-widest`}>
                      {meta.label}
                    </Badge>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Editar usuário">
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => del(u.id, u.name)}
                      data-testid={`del-user-${u.id}`}
                      disabled={!podeExcluir}
                      title={podeExcluir ? "Excluir usuário" : "Não é possível excluir o último Admin Master"}
                    >
                      <Trash2 size={14} className={podeExcluir ? "text-destructive" : "text-muted-foreground"} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Modal Editar Usuário */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Editar usuário {editingUser?.id === admin?.id ? "(você)" : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-tag">Nome completo</Label>
              <Input data-testid="edit-user-nome" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <Label className="label-tag">E-mail</Label>
              <Input data-testid="edit-user-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div>
              <Label className="label-tag">Nova senha (opcional — deixe vazio para não alterar)</Label>
              <Input data-testid="edit-user-senha" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="•••••" />
            </div>
            <div>
              <Label className="label-tag">Nível de acesso</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OPERACIONAL">Operacional</SelectItem>
                  <SelectItem value="GERENCIA">Gerência</SelectItem>
                  <SelectItem value="ADMIN_MASTER">Admin Master</SelectItem>
                </SelectContent>
              </Select>
              {editingUser?.role === "ADMIN_MASTER" && totalMasters <= 1 && editForm.role !== "ADMIN_MASTER" && (
                <div className="text-xs text-destructive mt-2">
                  Impossível rebaixar: precisa haver ao menos um Admin Master ativo.
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button data-testid="edit-user-salvar" onClick={salvarEdicao} className="bg-foreground text-background hover:bg-foreground/90">Salvar alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
